import React from "react";
import { Link } from "react-router-dom";
import { Play, Eye, Radio } from "lucide-react";
import { optimizeImageUrl, CARD_IMAGE_WIDTH, CARD_IMAGE_WIDTH_2X } from "../lib/images";
import { getLiveViewerCount } from "../lib/liveViewers";

function fadeDelay(index = 0) {
  return { animationDelay: `${Math.min(index * 45, 360)}ms` };
}

export function ProgramCard({ program, index = 0 }) {
  return (
    <div className="tv-fade-in" style={fadeDelay(index)}>
      <Link to={`/programlar/${program.slug}`} className="tv-card group block h-full">
        <div className="relative aspect-[16/11] overflow-hidden">
          <img
            src={optimizeImageUrl(program.coverImage, CARD_IMAGE_WIDTH)}
            srcSet={`${optimizeImageUrl(program.coverImage, CARD_IMAGE_WIDTH)} 1x, ${optimizeImageUrl(program.coverImage, CARD_IMAGE_WIDTH_2X)} 2x`}
            alt={program.name}
            loading="lazy"
            decoding="async"
            className="zoom-img w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#10111f] via-[#10111f]/20 to-transparent" />
          <div className="absolute top-3 left-3 flex items-center gap-2">
            <span className="tv-badge">{program.day}</span>
          </div>
          <span className="absolute top-3 right-3 text-xs font-semibold text-white bg-black/50 rounded-md px-2 py-1">
            {program.time}
          </span>
        </div>
        <div className="p-4">
          <h3 className="text-white font-semibold font-display group-hover:text-[var(--tv-purple-bright)] transition-colors">
            {program.name}
          </h3>
          <p className="text-sm text-[var(--tv-muted)] mt-1.5 line-clamp-2 leading-relaxed">{program.description}</p>
          <span className="mt-4 inline-flex w-full items-center justify-center rounded-lg border border-[var(--tv-border)] py-2 text-sm text-[var(--tv-text)] group-hover:border-[rgba(168,85,247,0.5)] group-hover:bg-[rgba(139,92,246,0.08)] transition-colors">
            Detay
          </span>
        </div>
      </Link>
    </div>
  );
}

export function VideoCard({ video, index = 0, onPlay, linkToDetail = false }) {
  const cardInner = (
    <>
      <div className="relative aspect-video overflow-hidden">
        <img
          src={optimizeImageUrl(video.coverImage, CARD_IMAGE_WIDTH)}
          srcSet={`${optimizeImageUrl(video.coverImage, CARD_IMAGE_WIDTH)} 1x, ${optimizeImageUrl(video.coverImage, CARD_IMAGE_WIDTH_2X)} 2x`}
          alt={video.title}
          loading="lazy"
          decoding="async"
          className="zoom-img w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/25 group-hover:bg-black/45 transition-colors" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-12 h-12 rounded-full bg-[rgba(139,92,246,0.85)] flex items-center justify-center scale-90 group-hover:scale-100 transition-transform duration-300 shadow-[0_0_24px_rgba(168,85,247,0.7)]">
            <Play size={20} className="text-white ml-0.5" fill="white" />
          </div>
        </div>
        {video.duration && (
          <span className="absolute bottom-2 right-2 text-xs font-medium text-white bg-black/70 rounded px-1.5 py-0.5">
            {video.duration}
          </span>
        )}
      </div>
      <div className="p-4">
        <h3 className="text-white font-semibold text-[15px] group-hover:text-[var(--tv-purple-bright)] transition-colors line-clamp-1">
          {video.title}
        </h3>
        <p className="text-sm text-[var(--tv-muted)] mt-1.5 line-clamp-2 leading-relaxed">{video.description}</p>
        <div className="flex items-center gap-2 mt-3 text-xs text-[var(--tv-muted)]">
          {video.publishedAt && <span>{video.publishedAt}</span>}
          {video.views && <span className="flex items-center gap-1">• <Eye size={12} /> {video.views}</span>}
        </div>
        {linkToDetail && (
          <span className="mt-4 inline-flex w-full items-center justify-center rounded-lg border border-[var(--tv-border)] py-2 text-sm text-[var(--tv-text)] group-hover:border-[rgba(168,85,247,0.5)] group-hover:bg-[rgba(139,92,246,0.08)] transition-colors">
            Detay
          </span>
        )}
      </div>
    </>
  );

  if (linkToDetail) {
    return (
      <Link
        to={`/videolar/${video.id}`}
        className="tv-fade-in tv-card group block h-full text-left w-full"
        style={fadeDelay(index)}
      >
        {cardInner}
      </Link>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onPlay?.(video)}
      className="tv-fade-in tv-card group text-left w-full h-full"
      style={fadeDelay(index)}
    >
      {cardInner}
    </button>
  );
}

export function LiveCard({ stream, index = 0, onOpen }) {
  return (
    <button
      type="button"
      onClick={() => onOpen(stream)}
      className="tv-fade-in tv-card group text-left w-full h-full"
      style={fadeDelay(index)}
    >
      <div className="relative aspect-video overflow-hidden">
        <img
          src={optimizeImageUrl(stream.coverImage, CARD_IMAGE_WIDTH)}
          srcSet={`${optimizeImageUrl(stream.coverImage, CARD_IMAGE_WIDTH)} 1x, ${optimizeImageUrl(stream.coverImage, CARD_IMAGE_WIDTH_2X)} 2x`}
          alt={stream.title}
          loading="lazy"
          decoding="async"
          className="zoom-img w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/20" />
        <div className="absolute top-3 left-3">
          {stream.isActive ? (
            <span className="tv-badge-live"><span className="w-1.5 h-1.5 rounded-full bg-white live-dot" /> CANLI</span>
          ) : (
            <span className="tv-badge">YAKINDA</span>
          )}
        </div>
        <span className="absolute top-3 right-3 flex items-center gap-1 text-xs font-medium text-white bg-black/60 rounded px-2 py-1">
          <Eye size={12} /> {getLiveViewerCount(stream)}
        </span>
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="w-12 h-12 rounded-full bg-[rgba(220,38,38,0.9)] flex items-center justify-center shadow-[0_0_24px_rgba(239,68,68,0.7)]">
            <Radio size={20} className="text-white" />
          </div>
        </div>
      </div>
      <div className="p-4">
        <h3 className="text-white font-semibold text-[15px] group-hover:text-[var(--tv-purple-bright)] transition-colors">
          {stream.title}
        </h3>
        <p className="text-sm text-[var(--tv-muted)] mt-1.5 line-clamp-1 leading-relaxed">{stream.description}</p>
      </div>
    </button>
  );
}

export function RowHeader({ title, to, linkLabel = "Tümü" }) {
  return (
    <div className="flex items-center justify-between mb-6">
      <h2 className="tv-section-title text-white">
        <span className="tv-section-bar" /> {title}
      </h2>
      {to && (
        <Link to={to} className="text-sm text-[var(--tv-muted)] hover:text-[var(--tv-purple-bright)] transition-colors inline-flex items-center gap-1.5 group">
          {linkLabel}
          <span className="transition-transform group-hover:translate-x-1">→</span>
        </Link>
      )}
    </div>
  );
}
